import React from "react";
import {NavLink, useHistory} from "react-router-dom";
// Chakra imports
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl, FormErrorMessage,
    FormLabel,
    Heading,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useColorModeValue, useToast,
} from "@chakra-ui/react";
// Custom components
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/landingpage-background.jpg";
import {MdOutlineRemoveRedEye} from "react-icons/md";
import {RiEyeCloseLine} from "react-icons/ri";
import {useForm} from "react-hook-form";
import { jwtDecode } from "jwt-decode";
import {postData, setAuthorizationToken} from "../../../services/api";
import Footer from "../../../components/footer/FooterAuth";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

function Login() {
    const history = useHistory();
    const toast = useToast()
    const MySwal = withReactContent(Swal)

    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = "gray.400";
    const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
    const textColorBrand = useColorModeValue("brand.500", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    const [show, setShow] = React.useState(false);

    const {
        handleSubmit,
        register,
        formState: {errors, isSubmitting},
    } = useForm()

    const handleClick = () => setShow(!show);

    function onSubmit(values) {
        return new Promise(async (resolve) => {
            try {
                let response = await postData('/api/login', values);
                if (response.token) {
                    // Save the token in localStorage
                    setAuthorizationToken(response.token);

                    const decoded = jwtDecode(response.token);
                    const userGroup = decoded.group;
                    // localStorage.setItem('group', userGroup);

                    // Redirect or handle successful login
                    history.push(userGroup === 1 ? `/admin/dashboard` : `/user/dashboard`);
                } else {
                    toast({
                        title: 'Login leider nicht erfolgreich, bitte versuchen Sie es erneut.',
                        status: 'error',
                        isClosable: true,
                    })
                }
                resolve();
            } catch (error) {
                if (error.response && error.response.status === 403) {
                    MySwal.fire({
                        title: <p>Registrierung nicht verifiziert</p>,
                        html: <p>Deine Registrierung wurde noch nicht verifiziert. Wir haben Dir erneut eine Email zur Bestätigung der
                            Anmeldung gesendet. Bitte folge den Anweisungen in der Email.</p>,
                        confirmButtonText: 'OK',
                        icon: 'info',
                    })
                } else {
                    toast({
                        title: 'Login leider nicht erfolgreich, bitte versuchen Sie es erneut.',
                        status: 'error',
                        isClosable: true,
                    })
                }
                resolve();
            }
        })
    }

    return (
        <Box>
            <DefaultAuth illustrationBackground={illustration} image={illustration}>
                <Flex
                    maxW={{base: "100%", md: "500px"}}
                    w={{base: "100%", md: "40vw"}}
                    mx={{base: "auto", lg: "0px"}}
                    me='auto'
                    h='100%'
                    alignItems='start'
                    justifyContent='center'
                    mb={{base: "30px", md: "60px"}}
                    px={{base: "25px", md: "0px"}}
                    mt={{base: "40px", md: "14vh"}}
                    flexDirection='column'>
                    <Box me='auto'>
                        <Heading color={textColor} fontSize='36px' mb='10px'>
                            Willkommen
                        </Heading>
                        <Text
                            mb='36px'
                            ms='4px'
                            color={textColorSecondary}
                            fontWeight='400'
                            fontSize='md'>
                            Bitte geben Sie Ihre Zugangsdaten ein um sich einzuloggen!
                        </Text>
                    </Box>
                    <Flex
                        zIndex='2'
                        direction='column'
                        w={{base: "100%", md: "420px"}}
                        maxW='100%'
                        borderRadius='15px'
                        mx={{base: "auto", lg: "unset"}}
                        me='auto'
                        mb={{base: "20px", md: "auto"}}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='md'
                                    fontWeight='500'
                                    color={textColor}
                                    mb='8px'
                                    htmlFor='email'
                                >
                                    Email-Adresse<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <Input
                                    id='email'
                                    variant='auth'
                                    fontSize='md'
                                    ms={{base: "0px", md: "0px"}}
                                    type='email'
                                    placeholder=''
                                    mb='24px'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('email', {
                                        required: 'Bitte geben Sie eine Email an',
                                    })}
                                />
                                <FormErrorMessage>
                                    {errors.email && errors.email.message}
                                </FormErrorMessage>
                                <FormLabel
                                    ms='4px'
                                    fontSize='md'
                                    fontWeight='500'
                                    color={textColor}
                                    display='flex'
                                    htmlFor='password'
                                >
                                    Passwort<Text color={brandStars}>*</Text>
                                </FormLabel>
                                <InputGroup size='md'>
                                    <Input
                                        id='password'
                                        fontSize='md'
                                        placeholder=''
                                        mb='24px'
                                        size='lg'
                                        type={show ? "text" : "password"}
                                        variant='auth'
                                        {...register('password', {
                                            required: 'Bitte geben Sie das Passwort an',
                                        })}
                                    />
                                    <InputRightElement display='flex' alignItems='center' mt='4px'>
                                        <Icon
                                            color={textColorSecondary}
                                            _hover={{cursor: "pointer"}}
                                            as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                                            onClick={handleClick}
                                        />
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors.password && errors.password.message}
                                </FormErrorMessage>
                                <Flex justifyContent='space-between' align='center' mb='24px'>
                                    <FormControl display='flex' alignItems='center'>
                                        <Checkbox
                                            id='remember-login'
                                            colorScheme='brandScheme'
                                            me='10px'
                                        />
                                        <FormLabel
                                            htmlFor='remember-login'
                                            mb='0'
                                            fontWeight='normal'
                                            color={textColor}
                                            fontSize='md'>
                                            Eingeloggt bleiben
                                        </FormLabel>
                                    </FormControl>
                                    <NavLink to='/auth/forgot-password'>
                                        <Text
                                            color={textColorBrand}
                                            fontSize='md'
                                            w='124px'
                                            fontWeight='500'>
                                            Passwort vergessen?
                                        </Text>
                                    </NavLink>
                                </Flex>
                                <Button
                                    fontSize='md'
                                    variant='brand'
                                    fontWeight='500'
                                    w='100%'
                                    h='50'
                                    mb='24px'
                                    isLoading={isSubmitting}
                                    type='submit'
                                >
                                    Anmelden
                                </Button>
                            </FormControl>
                        </form>
                        <Flex
                            flexDirection='column'
                            justifyContent='center'
                            alignItems='start'
                            maxW='100%'
                            mt='0px'>
                            <Text color={textColorDetails} fontWeight='400' fontSize='md'>
                                Noch keinen Account?
                                <NavLink to='/auth/register'>
                                    <Text
                                        color={textColorBrand}
                                        as='span'
                                        ms='5px'
                                        fontWeight='500'>
                                        Account anlegen
                                    </Text>
                                </NavLink>
                            </Text>
                        </Flex>
                    </Flex>
                </Flex>
            </DefaultAuth>

            <Footer/>
        </Box>
    );
}

export default Login;
